import { Colors, Text } from '@amelio/ui-library';

import type { MeetingTemplate, ThematicMeeting } from '@/types/thematic-meetings';

import { PlanSection } from '@/components/MeetingPlan/PlanSection';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { PreparationNotesEditor } from '@/pages/ThematicMeetings/Plan/components/PreparationNotesEditor';
import { QuestionsWithNotes } from '@/pages/ThematicMeetings/Plan/components/QuestionsWithNotes';
import { ReadOnlyNotes } from '@/pages/ThematicMeetings/Plan/components/ReadOnlyNotes';
import { getTextByLanguage } from '@/utils/localization';

interface PreparationSectionProps {
    meeting: ThematicMeeting;
    template: MeetingTemplate | undefined;
    disabled?: boolean;
    dataTestId?: string;
}

export const PreparationSection = ({
    meeting,
    template,
    disabled = false,
    dataTestId = 'thematic-meeting-preparation',
}: PreparationSectionProps) => {
    const language = useSelectedAppLanguage();
    const { t } = useAmelioTranslations();
    const questions = template?.preparationQuestions ?? [];

    return (
        <PlanSection
            title={t('thematicMeetings.plan.preparationTitle')}
            dataTestId={dataTestId}
        >
            <QuestionsWithNotes
                questions={questions.map((question) => getTextByLanguage(question, language))}
            >
                <Text
                    weight={'semibold'}
                    color={Colors.BASE_600}
                >
                    {t('thematicMeetings.plan.notesLabel')}
                </Text>

                {disabled ? (
                    <ReadOnlyNotes
                        content={meeting.preparationNotes}
                        emptyMessage={t('thematicMeetings.plan.notesEmptyCompleted')}
                        ariaLabel={t('thematicMeetings.plan.notesLabel')}
                        language={language}
                        dataTestId={`${dataTestId}-notes`}
                    />
                ) : (
                    <PreparationNotesEditor
                        key={meeting.id}
                        meeting={meeting}
                        language={language}
                        dataTestId={`${dataTestId}-notes`}
                    />
                )}
            </QuestionsWithNotes>
        </PlanSection>
    );
};
